import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import PhotoUpload from "@/components/PhotoUpload";

const SLOTS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export default function Schedule() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [date, setDate] = useState(undefined);
  const [slot, setSlot] = useState("");
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", street: "", city: "", state: "CA", zip: "", notes: "" });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ack, setAck] = useState({ preliminary: false, cancellation: false, appointment: false, tos: false, privacy: false });

  useEffect(() => {
    const d = sessionStorage.getItem("repair_draft");
    if (!d) { navigate("/"); return; }
    setDraft(JSON.parse(d));
  }, [navigate]);

  const dateStr = useMemo(() => {
    if (!date) return "";
    const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, "0"); const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [date]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email);
  const allAck = ack.preliminary && ack.cancellation && ack.appointment && ack.tos && ack.privacy;
  const isToday = useMemo(() => {
    if (!date) return false;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    return d.getTime() === t.getTime();
  }, [date]);

  const isSlotPast = (s) => {
    if (!isToday) return false;
    const [hh, mm] = s.split(":").map(Number);
    const now = new Date();
    const slotTime = new Date();
    slotTime.setHours(hh, mm, 0, 0);
    // Require at least 15 minutes of buffer so the customer can actually arrive
    return slotTime.getTime() <= now.getTime() + 15 * 60 * 1000;
  };

  useEffect(() => {
    if (slot && isSlotPast(slot)) setSlot("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const canSubmit = draft && dateStr && slot && !isSlotPast(slot) && customer.name.trim() && validEmail && customer.phone.trim() && customer.street.trim() && customer.city.trim() && customer.state.trim() && customer.zip.trim() && allAck;

  const submit = async () => {
    if (!canSubmit) { toast.error("Fill every required field first."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...draft,
        dropoff_date: dateStr,
        dropoff_time: slot,
        customer,
        photo_ids: photos.map((p) => p.id),
        acknowledgments: {
          preliminary_estimate: ack.preliminary,
          parts_cancellation_policy: ack.cancellation,
          appointment_policy: ack.appointment,
          terms_of_service: ack.tos,
          privacy_policy: ack.privacy,
          policy_version: "2026-02-draft",
          acknowledged_at: new Date().toISOString(),
          estimate_snapshot: draft.quote,
          estimate_ack_from_configurator: draft.estimate_ack || null,
        },
      });
      sessionStorage.removeItem("repair_draft");
      navigate(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to book. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!draft) return null;

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="overline">Step 4 of 6</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
          Pick a drop-off day & time.
        </h1>
        <p className="mt-2 text-slate-600">We'll email you separately to schedule pickup once your device is ready.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <div className="overline mb-3">Drop-off date</div>
            <div data-testid="dropoff-calendar-picker" className="flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  return d < today;
                }}
              />
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-baseline justify-between mb-3">
              <div className="overline">Drop-off time slot</div>
              {isToday && (
                <div className="text-xs text-slate-500" data-testid="today-hint">
                  Today — past slots are disabled
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {SLOTS.map((s) => {
                const past = isSlotPast(s);
                const active = slot === s;
                return (
                  <button
                    key={s}
                    data-testid={`dropoff-time-slot-${s}`}
                    onClick={() => !past && setSlot(s)}
                    disabled={past}
                    className={`px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition ${
                      past
                        ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed line-through decoration-slate-400"
                        : active
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
            {isToday && SLOTS.every((s) => isSlotPast(s)) && (
              <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="no-slots-today">
                No more slots today — please pick a future date.
              </p>
            )}
          </div>

          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <div className="overline mb-3">Your details</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" value={customer.name} onChange={(v) => setCustomer({ ...customer, name: v })} testid="customer-name" />
              <Field label="Email" value={customer.email} onChange={(v) => setCustomer({ ...customer, email: v })} testid="customer-email" type="email" />
              <Field label="Phone" value={customer.phone} onChange={(v) => setCustomer({ ...customer, phone: v })} testid="customer-phone" />
              <Field label="Street address" value={customer.street} onChange={(v) => setCustomer({ ...customer, street: v })} testid="customer-street" />
              <Field label="City" value={customer.city} onChange={(v) => setCustomer({ ...customer, city: v })} testid="customer-city" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="State" value={customer.state} onChange={(v) => setCustomer({ ...customer, state: v })} testid="customer-state" />
                <Field label="ZIP" value={customer.zip} onChange={(v) => setCustomer({ ...customer, zip: v })} testid="customer-zip" />
              </div>
              <label className="block sm:col-span-2">
                <span className="block text-sm font-medium text-slate-700 mb-1.5">Notes for the tech (optional)</span>
                <input data-testid="customer-notes" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900" />
                <span className="mt-1 block text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Security Notice: Do not enter passwords, PINs, account credentials, Social Security numbers, payment-card information, or other sensitive information in this field.
                </span>
              </label>
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <div className="overline mb-3">Required acknowledgments</div>
            <div className="space-y-3 text-sm text-slate-700">
              <AckRow testid="ack-preliminary" checked={ack.preliminary} onChange={(v) => setAck({ ...ack, preliminary: v })}
                label={<>I understand that the online price shown is a <strong>preliminary estimate</strong> and final pricing is subject to physical inspection.</>} />
              <AckRow testid="ack-cancellation" checked={ack.cancellation} onChange={(v) => setAck({ ...ack, cancellation: v })}
                label={<>I have reviewed and agree to the <a href="/policy/parts-cancellation" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline">Parts Order &amp; Cancellation Policy</a>.</>} />
              <AckRow testid="ack-appointment" checked={ack.appointment} onChange={(v) => setAck({ ...ack, appointment: v })}
                label={<>I agree to The Repair Nook <a href="/policy/appointment" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline">Appointment &amp; Cancellation Policy</a>.</>} />
              <AckRow testid="ack-tos" checked={ack.tos} onChange={(v) => setAck({ ...ack, tos: v })}
                label={<>I have read and agree to the <a href="/policy/terms" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline">Terms of Service</a>.</>} />
              <AckRow testid="ack-privacy" checked={ack.privacy} onChange={(v) => setAck({ ...ack, privacy: v })}
                label={<>I acknowledge the <a href="/policy/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-900 underline">Privacy Policy</a>.</>} />
            </div>
          </div>

          <div className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
            <div className="flex items-baseline justify-between mb-3">
              <div className="overline">Photos of the damage <span className="normal-case tracking-normal text-slate-400 text-[10px] font-normal">(optional but helpful)</span></div>
              <div className="text-xs text-slate-400 font-mono">{photos.length}/6</div>
            </div>
            <PhotoUpload photos={photos} setPhotos={setPhotos} />
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-24 p-6 md:p-8 rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="overline mb-3">Repair summary</div>
            <div className="text-sm text-slate-700 space-y-1">
              <div><span className="text-slate-500">Device:</span> <span className="font-medium">{draft.brand} {draft.model_name}</span></div>
              <div><span className="text-slate-500">Fixes:</span> <span className="font-medium">{draft.services.length}</span></div>
              {draft.rush && <div className="text-amber-700 font-medium">Rush service</div>}
            </div>
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Parts (pay online next)</div>
              <div className="font-mono text-lg font-bold text-amber-900">${draft.quote.parts_total.toFixed(2)}</div>
            </div>
            <div className="mt-2 rounded-xl bg-sky-50 border border-sky-200 p-3">
              <div className="text-[10px] uppercase tracking-widest text-sky-700 font-semibold">Labor (pay at pickup)</div>
              <div className="font-mono text-lg font-bold text-sky-900">${draft.quote.labor_total.toFixed(2)}</div>
            </div>
            <button
              data-testid="schedule-submit-btn"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition disabled:opacity-40"
            >
              {submitting ? "Booking…" : <>Continue to checkout <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AckRow({ testid, checked, onChange, label }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300" />
      <span>{label}</span>
    </label>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900"
      />
    </label>
  );
}
